import { useState } from 'react';

import { changerMotDePasse } from '../api/motdepasse';
import { useSession } from '../app/SessionContext';

const CLASSE_CHAMP =
  'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';
const CLASSE_LIBELLE = 'block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1';

/**
 * Mon compte : ce que le serveur sait de moi, et le changement de mon mot de passe.
 *
 * L'ancien mot de passe est exigé — un cookie volé ne doit pas suffire à verrouiller
 * le compte de son propriétaire. Après le changement, les autres sessions tombent.
 */
export function MonCompte() {
  const { utilisateur } = useSession();
  const [ancien, setAncien] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string>();
  const [confirmationSucces, setConfirmationSucces] = useState<string>();
  const [envoi, setEnvoi] = useState(false);

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();

    if (nouveau !== confirmation) {
      setErreur('Les deux nouveaux mots de passe ne correspondent pas.');
      return;
    }

    setEnvoi(true);
    setErreur(undefined);
    setConfirmationSucces(undefined);

    const resultat = await changerMotDePasse(ancien, nouveau);
    setEnvoi(false);

    if (resultat.ok) {
      setAncien('');
      setNouveau('');
      setConfirmation('');
      setConfirmationSucces(
        'Mot de passe changé. Vos autres sessions ont été fermées ; celle-ci reste ouverte.',
      );
    } else {
      setErreur(resultat.message ?? 'Changement impossible.');
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Mon compte</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Nom</dt>
            <dd className="text-slate-800 font-medium">{utilisateur?.nom ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Adresse</dt>
            <dd className="text-slate-800">{utilisateur?.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Rôle</dt>
            <dd className="text-slate-800">
              {utilisateur?.role === 'admin' ? 'Administrateur' : 'Prestataire'}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Compte ouvert depuis</dt>
            <dd className="text-slate-800 mono">{utilisateur?.creeLe?.slice(0, 10) ?? '—'}</dd>
          </div>
        </dl>
      </div>

      <form
        onSubmit={(evenement) => void soumettre(evenement)}
        className="bg-white rounded-lg border border-slate-200 p-5"
        aria-labelledby="titre-mot-de-passe"
      >
        <h2 id="titre-mot-de-passe" className="text-sm font-semibold text-slate-800 mb-1">
          Changer mon mot de passe
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Vos autres sessions seront fermées. Celle-ci reste ouverte.
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="ancien-mot-de-passe" className={CLASSE_LIBELLE}>
              Mot de passe actuel
            </label>
            <input
              id="ancien-mot-de-passe"
              type="password"
              value={ancien}
              onChange={(evenement) => setAncien(evenement.target.value)}
              className={CLASSE_CHAMP}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label htmlFor="nouveau-mot-de-passe" className={CLASSE_LIBELLE}>
              Nouveau mot de passe
            </label>
            <input
              id="nouveau-mot-de-passe"
              type="password"
              value={nouveau}
              onChange={(evenement) => setNouveau(evenement.target.value)}
              className={CLASSE_CHAMP}
              autoComplete="new-password"
              minLength={10}
              required
            />
            <p className="mt-1 text-xs text-slate-500">Dix caractères minimum.</p>
          </div>
          <div>
            <label htmlFor="confirmation-mot-de-passe" className={CLASSE_LIBELLE}>
              Confirmer le nouveau mot de passe
            </label>
            <input
              id="confirmation-mot-de-passe"
              type="password"
              value={confirmation}
              onChange={(evenement) => setConfirmation(evenement.target.value)}
              className={CLASSE_CHAMP}
              autoComplete="new-password"
              minLength={10}
              required
            />
          </div>
        </div>

        {erreur && (
          <p role="alert" className="mt-4 text-sm text-red-700">
            {erreur}
          </p>
        )}
        {confirmationSucces && (
          <p role="status" className="mt-4 text-sm text-green-700">
            {confirmationSucces}
          </p>
        )}

        <div className="mt-4">
          <button
            type="submit"
            disabled={envoi}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60"
          >
            {envoi ? 'Enregistrement…' : 'Changer le mot de passe'}
          </button>
        </div>
      </form>
    </div>
  );
}
